import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// "ResizeObserver loop completed with undelivered notifications" (and the
// older Chrome wording "ResizeObserver loop limit exceeded") is a benign
// browser message, not a real error: several UI primitives used across the
// app (Radix Select/Popover, the searchable combobox, etc.) watch their own
// size with a ResizeObserver, and the browser emits this warning whenever a
// callback can't finish notifying within one animation frame - it retries
// automatically next frame with no visible effect. The app underneath is not
// broken when this fires.
//
// The first attempt at silencing this (a window "error" listener calling
// stopImmediatePropagation) did not work: react-error-overlay's own listener
// is registered by a separate webpack-dev-server client bundle that loads
// and runs BEFORE this file, so it always receives the event first
// regardless of capture phase - listeners on the same node/phase fire in
// registration order, and ours could never be registered earlier than
// theirs. The actual fix has to stop the browser from ever reaching that
// loop-limit condition in the first place, which is done here by wrapping
// every ResizeObserver callback so it runs on the next animation frame
// instead of synchronously - this is the standard fix for this exact
// browser behavior (see WICG/resize-observer#38) and must run before any
// component constructs a ResizeObserver, which is why it sits at the very
// top of the app's entry point, before React ever renders anything.
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined" && !window.ResizeObserver.__patchedForLoopLimit) {
  const NativeResizeObserver = window.ResizeObserver;
  class PatchedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let scheduled = false;
      super((entries, observer) => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          callback(entries, observer);
        });
      });
    }
  }
  PatchedResizeObserver.__patchedForLoopLimit = true;
  window.ResizeObserver = PatchedResizeObserver;
}

// Kept as a harmless second layer - even with the patch above, older
// third-party bundles that captured a reference to the native
// ResizeObserver before this file ran (rare, but possible with certain
// dependency pre-bundling) could still trigger the browser message; this
// swallows only that exact known-benign text and lets every other runtime
// error through unchanged.
window.addEventListener("error", (event) => {
  const message = event?.message || event?.error?.message || "";
  if (/ResizeObserver loop (limit exceeded|completed with undelivered notifications)/.test(message)) {
    event.stopImmediatePropagation();
    event.preventDefault?.();
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
